import * as React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { DownOutlined, UpOutlined } from '@ant-design/icons';

import { useSelector } from 'react-redux';

const CloseButton = styled.button`
  border: 0;
  background-color: transparent;
  color: black;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  display: flex;
  align-items: center;
  > span {
    margin-top: 2px;
    margin-right: 4px;
    font-size: 14px;
  }
`;

const Details = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const Detail = styled.li`
  color: black;
  font-size: 16px;
  margin-bottom: 4px;
  list-style: none;
`;

const Title = styled.p`
  font-family: ${({ theme }) => theme.fonts.semibold};
  font-size: 24px;
  letter-spacing: 0.94px;
  margin-bottom: 0;
`;

const StyledContact = styled.div`
  display: flex;
  flex-direction: column;
  padding: 13px 40px 13px;
  width: 100%;
  align-items: flex-start;
`;

const Top = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: flex-start;
`;

function Contact({ activity }) {
  const { connectedContact } = useSelector(state => state.dialer);

  const skipFields = React.useMemo(() => ['first_name', 'last_name'], []);

  const renderDetail = React.useCallback(
    (el, skipVal) => {
      if (skipFields.includes(el) || el === skipVal) {
        return null;
      }

      const label = activity?.localized_presented_contact_details?.[el];
      let text = '';
      try {
        text = connectedContact[el];
      } catch (e) {}
      if (typeof text === 'boolean') {
        text = text.toString();
      }
      if (text) {
        return (
          <Detail key={el}>
            {label}: {text}
          </Detail>
        );
      }
      return null;
    },
    [skipFields, connectedContact, activity],
  );

  const fullName = React.useMemo(
    () =>
      connectedContact?.first_name +
      (activity?.localized_presented_contact_details?.['last_name']
        ? ` ${connectedContact?.last_name}`
        : ''),
    [connectedContact, activity],
  );

  const firstCustomField = React.useMemo(() => {
    const filteredFields = activity?.presented_contact_details?.filter(
      el => !skipFields.includes(el) && !!connectedContact[el],
    );
    return (filteredFields?.length ?? 0) > 0 ? filteredFields[0] : null;
  }, [activity?.presented_contact_details, skipFields, connectedContact]);

  const topNodeContent = (() => {
    return (
      <Top>
        <Title>{fullName}</Title>
        <div style={{ marginTop: 8 }}>{renderDetail(firstCustomField)}</div>
      </Top>
    );
  })();

  const showDetailsButton = React.useMemo(() => {
    const filteredFields = activity?.presented_contact_details?.filter(
      el => !skipFields.includes(el),
    );

    if (!connectedContact || (filteredFields?.length ?? 0) === 0) {
      return false;
    }

    const availableValues = filteredFields.filter(el => !!connectedContact[el]);

    return (availableValues?.length ?? 0) > 1;
  }, [connectedContact, activity?.presented_contact_details, skipFields]);

  const [detailsOpen, setDetailsOpen] = React.useState(false);

  if (detailsOpen) {
    return (
      <StyledContact>
        <Top>
          {topNodeContent}
          <Details>
            {connectedContact &&
              (activity?.presented_contact_details?.slice(1) ?? []).map(e =>
                renderDetail(e, firstCustomField),
              )}
          </Details>

          <CloseButton onClick={() => setDetailsOpen(false)}>
            <UpOutlined /> Hide Additional Contact Details
          </CloseButton>
        </Top>
      </StyledContact>
    );
  }

  return (
    <StyledContact>
      <Top>{topNodeContent}</Top>
      {showDetailsButton && (
        <CloseButton style={{ marginTop: 8 }} onClick={() => setDetailsOpen(true)}>
          <DownOutlined /> Show Additional Contact Details
        </CloseButton>
      )}
    </StyledContact>
  );
}

Contact.propTypes = {
  activity: PropTypes.object.isRequired,
};

Contact.defaultProps = {};

export default Contact;
